"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import type { Paginated } from "@/lib/api/types";

/**
 * Consuming the gateway's cursor pagination.
 *
 * The console used to ask each list for a fixed slab — `limit: 50`, sometimes
 * 100 or 300 — and render whatever came back. Row 51 was simply absent, with
 * nothing on screen to say so. That is invisible against the mock corpus, which
 * holds four scans and eleven findings, and it is a silent data loss against a
 * real tenant.
 *
 * `useInfiniteQuery` rather than a hand-rolled accumulator because the cache
 * key, the in-flight guard and the page list are exactly what it exists to
 * hold. What this adds is the shape: `items` flattened for a table that pages
 * client-side over what is loaded, and `hasMore` so the surface can say there
 * is more rather than implying there is not.
 */
export interface CursorList<T> {
  items: T[];
  /**
   * A cursor came back with a NON-EMPTY page, so another page may exist. Not
   * "does exist": the server returns a cursor on any FULL page (S-708, 2.796),
   * so a list exactly one page long offers more and then reads an empty page.
   */
  hasMore: boolean;
  /**
   * 2.796: the last page read was an EMPTY follow-up page — the list ended
   * exactly on a page boundary. The surface says there is nothing further
   * rather than leaving a "Load more" that already answered nothing.
   */
  endedEmpty: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  isLoading: boolean;
  error: unknown;
}

export function useCursorList<T>(
  key: readonly unknown[],
  fetchPage: (cursor?: string) => Promise<Paginated<T>>,
  options: { enabled?: boolean } = {},
): CursorList<T> {
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    // Returning undefined is what stops `hasNextPage`: an absent cursor, or an
    // empty page (nextPageCursor, 2.796).
    getNextPageParam: nextPageCursor,
    enabled: options.enabled ?? true,
  });

  return {
    items: query.data?.pages.flatMap((p) => p.items) ?? [],
    hasMore: Boolean(query.hasNextPage),
    endedEmpty: endedOnEmptyPage(query.data?.pages),
    loadMore: () => {
      // Only the exhausted check. There WAS an `!isFetchingNextPage` guard here
      // and it did nothing — MEASURED by firing loadMore three times in one
      // tick, with and without it: identical both ways, three fetches of the
      // same cursor and 100 items. The flag has not flipped within the tick, so
      // the guard never fires; and react-query keys pages by `pageParam`, so a
      // repeated fetch cannot append twice in any case.
      //
      // What actually prevents the double-click is the button's own
      // `disabled={isLoading}` in LoadMore. Leaving a guard that measurably
      // does nothing would be worse than none: the next reader would trust it.
      if (query.hasNextPage) void query.fetchNextPage();
    },
    isLoadingMore: query.isFetchingNextPage,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * 2.796 — the cursor to ask for next, or undefined when there is no next page.
 *
 * The server returns a cursor on any FULL page, not only when more rows exist
 * (S-708's listReports, measured by the product lane's trial assembly), so a
 * list exactly one page long answers "more" and then an empty page. An EMPTY
 * page therefore ends the list whatever cursor it carries: it cannot be
 * followed into a loop, and a surface never offers more after a page that had
 * none.
 */
export function nextPageCursor<C>(last: { items: readonly unknown[]; nextCursor?: C }): C | undefined {
  return last.items.length === 0 ? undefined : last.nextCursor;
}

/** 2.796: true when a FOLLOW-UP page (not the first) came back empty. */
export function endedOnEmptyPage(pages: readonly { items: readonly unknown[] }[] | undefined): boolean {
  return !!pages && pages.length > 1 && pages[pages.length - 1].items.length === 0;
}
